/**
 * Customer content collection — two steps.
 *
 *   1. Name, photos and/or clips, release. Submitted, then saved.
 *   2. Thank you, plus an optional star rating and a sentence, which attaches to
 *      the record made in step 1 by the token it handed back.
 *
 * Files never travel through our app: for each one we ask the app proxy for a
 * signed Shopify upload target and POST the file straight there. A phone video
 * is tens of megabytes and would not survive the proxy — and XHR gives us a
 * real progress bar, which fetch does not.
 */
(function () {
  const root = document.querySelector('[data-content-collect]');
  if (!root) return;

  const API = '/apps/measurements';
  const t = JSON.parse(root.dataset.i18n || '{}');
  const MAX_FILES = 12;

  const $ = (sel) => root.querySelector(sel);
  const stepOne = $('[data-step="1"]');
  const stepTwo = $('[data-step="2"]');
  const fileInput = $('[data-files]');
  const list = $('[data-file-list]');
  const submitBtn = $('[data-submit]');
  const errorBox = $('[data-error]');
  const progressWrap = $('[data-progress]');
  const progressBar = $('[data-progress-bar]');
  const progressLabel = $('[data-progress-label]');

  /** @type {{file: File, id: number}[]} */
  let chosen = [];
  let nextId = 1;
  let token = null;
  let rating = 0;
  let busy = false;

  // A personal link from customer service (?k=…) says whose upload this is:
  // name and order come from the order, the files land on its record in the
  // CS dashboard. An unknown or broken link falls back to the plain form.
  const linkToken = new URLSearchParams(window.location.search).get('k') || '';
  let linked = null;

  const bytes = (n) => n > 1024 * 1024
    ? `${(n / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1024))} KB`;

  function showError(message) {
    errorBox.textContent = message || '';
    errorBox.hidden = !message;
    if (message) errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function renderList() {
    list.innerHTML = '';
    chosen.forEach((entry) => {
      const row = document.createElement('li');
      row.className = 'cc-file';

      const thumb = document.createElement('div');
      thumb.className = 'cc-file__thumb';
      if (entry.file.type.startsWith('image/')) {
        const img = document.createElement('img');
        img.alt = '';
        img.src = URL.createObjectURL(entry.file);
        img.onload = () => URL.revokeObjectURL(img.src);
        thumb.appendChild(img);
      } else {
        thumb.textContent = '▶';
        thumb.classList.add('cc-file__thumb--video');
      }

      const meta = document.createElement('div');
      meta.className = 'cc-file__meta';
      const name = document.createElement('span');
      name.className = 'cc-file__name';
      name.textContent = entry.file.name;
      const size = document.createElement('span');
      size.className = 'cc-file__size';
      size.textContent = bytes(entry.file.size);
      meta.append(name, size);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'cc-file__remove';
      remove.setAttribute('aria-label', t.remove || 'Entfernen');
      remove.textContent = '×';
      remove.addEventListener('click', () => {
        chosen = chosen.filter((x) => x.id !== entry.id);
        renderList();
      });

      row.append(thumb, meta, remove);
      list.appendChild(row);
    });
    list.hidden = chosen.length === 0;
    updateSubmit();
  }

  function updateSubmit() {
    const named = $('[data-name]').value.trim().length > 0;
    submitBtn.disabled = busy || !chosen.length || !named;
  }

  fileInput.addEventListener('change', () => {
    showError('');
    const incoming = Array.from(fileInput.files || []);
    const room = MAX_FILES - chosen.length;
    if (incoming.length > room) {
      showError((t.tooMany || 'Höchstens {n} Dateien.').replace('{n}', MAX_FILES));
    }
    incoming.slice(0, Math.max(0, room)).forEach((file) => {
      chosen.push({ file, id: nextId++ });
    });
    // Let the same file be picked again after removing it.
    fileInput.value = '';
    renderList();
  });

  root.querySelectorAll('[data-name]').forEach((el) => {
    el.addEventListener('input', updateSubmit);
    el.addEventListener('change', updateSubmit);
  });

  function setProgress(done, total, label) {
    progressWrap.hidden = false;
    const pct = total ? Math.round((done / total) * 100) : 0;
    progressBar.style.width = pct + '%';
    progressLabel.textContent = label;
  }

  async function uploadOne(file, onProgress) {
    const res = await fetch(`${API}?action=content-upload-target`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, mimeType: file.type, fileSize: file.size })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.target) throw new Error(data.error || (t.uploadFailed || 'Upload fehlgeschlagen.'));

    const form = new FormData();
    data.target.parameters.forEach((p) => form.append(p.name, p.value));
    form.append('file', file, file.name);

    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', data.target.url, true);
      xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total); };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300)
        ? resolve() : reject(new Error('upload_failed_' + xhr.status));
      xhr.onerror = () => reject(new Error('upload_network'));
      xhr.send(form);
    });

    return { resourceUrl: data.target.resourceUrl, kind: data.kind, filename: file.name };
  }

  async function applyPersonalLink() {
    if (!linkToken) return;
    try {
      const res = await fetch(`${API}?action=content-prefill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ linkToken })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) return;
      linked = data;
      // The server files the upload under the order; the name field only has
      // to satisfy the form, so it is never empty.
      $('[data-name]').value = data.firstName || data.orderName || '-';
      $('[data-order]').value = data.orderName || '';
      root.querySelectorAll('[data-identity]').forEach((el) => { el.hidden = true; });
      const greeting = $('[data-greeting-text]');
      const template = root.dataset.greeting || '';
      if (greeting && template) {
        greeting.textContent = template
          .replace('{name}', data.firstName || '')
          .replace('{order}', data.orderName || '')
          .replace(/\s+!/, '!')
          .trim();
        greeting.hidden = false;
      }
      updateSubmit();
    } catch (e) {
      // The plain form still works.
    }
  }
  applyPersonalLink();

  submitBtn.addEventListener('click', async () => {
    if (busy) return;
    busy = true;
    showError('');
    updateSubmit();

    const total = chosen.length;
    const uploads = [];
    try {
      for (let i = 0; i < chosen.length; i++) {
        const entry = chosen[i];
        setProgress(i, total, (t.uploading || 'Datei {i} von {n} …')
          .replace('{i}', String(i + 1)).replace('{n}', String(total)));
        uploads.push(await uploadOne(entry.file, (fraction) => {
          const done = i + fraction;
          progressBar.style.width = Math.round((done / total) * 100) + '%';
        }));
      }
      setProgress(total, total, t.saving || 'Wird gespeichert …');

      const res = await fetch(`${API}?action=submit-content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: $('[data-name]').value.trim(),
          email: $('[data-email]').value.trim(),
          orderRef: $('[data-order]').value.trim(),
          market: root.dataset.market || 'de',
          consent: true,
          consentText: root.dataset.consentText || '',
          linkToken: linked ? linkToken : '',
          uploads
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || (t.saveFailed || 'Speichern fehlgeschlagen.'));

      token = data.token;
      progressWrap.hidden = true;
      stepOne.hidden = true;
      stepTwo.hidden = false;
      stepTwo.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (window.gtag) window.gtag('event', 'content_submitted', { files: total });
    } catch (e) {
      progressWrap.hidden = true;
      showError(e.message === 'consent_required'
        ? (t.consentRequired || 'Bitte bestätige die Einwilligung.')
        : (t.uploadFailed || 'Das hat nicht geklappt. Bitte versuch es noch einmal.'));
    } finally {
      busy = false;
      updateSubmit();
    }
  });

  /* ── Step two: the review ─────────────────────────────────────────────── */

  const starButtons = Array.from(root.querySelectorAll('[data-star]'));
  function paintStars(value) {
    starButtons.forEach((b) => {
      const on = Number(b.dataset.star) <= value;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-checked', String(Number(b.dataset.star) === value));
    });
  }
  starButtons.forEach((b) => {
    b.addEventListener('click', () => {
      rating = Number(b.dataset.star);
      paintStars(rating);
      $('[data-review-submit]').disabled = false;
    });
  });

  const reviewSubmit = $('[data-review-submit]');
  if (reviewSubmit) {
    reviewSubmit.addEventListener('click', async () => {
      if (!token || !rating) return;
      reviewSubmit.disabled = true;
      try {
        await fetch(`${API}?action=submit-content-review`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, rating, text: $('[data-review-text]').value.trim() })
        });
        if (window.gtag) window.gtag('event', 'content_review_submitted', { rating });
      } catch (e) {
        // The upload is already safe; a failed review is not worth a scary
        // message, and the thank-you below says the same thing either way.
        console.error('review submit failed', e);
      }
      $('[data-review-form]').hidden = true;
      $('[data-review-done]').hidden = false;
      // Five stars here is someone happy enough to say so in public too.
      const tp = $('[data-trustpilot]');
      if (tp && rating === 5) {
        tp.hidden = false;
        const link = $('[data-trustpilot-link]');
        if (link) link.addEventListener('click', () => {
          if (window.gtag) window.gtag('event', 'content_trustpilot_click', { order: linked?.orderName || '' });
        });
      }
    });
  }
})();
